import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, Star, TrendingUp, Flame, Award } from 'lucide-react';
import MinerviniBadge from '@/components/MinerviniBadge';
import AbbrTip from '@/components/AbbrTip';
import usePriceStream from '@/hooks/usePriceStream';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREFS_KEY = 'dividrip:stage2Leaderboard:prefs';
const ALLOWED_LIMITS = [3, 5, 10];

const loadLimitPref = () => {
  try {
    const obj = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return ALLOWED_LIMITS.includes(obj.limit) ? obj.limit : 3;
  } catch { return 3; }
};

// Dashboard card: longest-held Minervini SEPA Lite passes + fresh Stage 2
// breakouts. Sits above the Capital Quality leaderboard; same admin gate.
export default function Stage2Leaderboard({ onStockClick, watchlist = [], onToggleWatchlist }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(loadLimitPref);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/minervini/leaderboard`, { params: { limit: 10 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ limit })); } catch { /* noop */ }
  }, [limit]);

  const veterans = useMemo(() => (data?.veterans || []).slice(0, limit), [data, limit]);
  const fresh = useMemo(() => (data?.fresh || []).slice(0, limit), [data, limit]);
  const tickers = useMemo(() => [...new Set([...veterans, ...fresh].map((r) => r.ticker))], [veterans, fresh]);
  const { prices: livePrices } = usePriceStream(tickers);

  if (error) return null;
  if (!loading && !data?.scan_day) return null;

  const rowProps = { onStockClick, watchlist, onToggleWatchlist, livePrices };

  return (
    <div className="rounded-xl border border-sky-500/50 bg-card p-4 sm:p-5" data-testid="stage2-leaderboard">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className="w-[18px] h-[18px] text-sky-500 shrink-0" />
          <h3 className="text-[18px] font-semibold tracking-tight truncate">Stage 2 Leaders - Minervini Trend</h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="inline-flex rounded-md border border-border" data-testid="stage2-leaderboard-size-toggle">
            {ALLOWED_LIMITS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setLimit(n)}
                data-testid={`stage2-leaderboard-limit-${n}`}
                aria-pressed={limit === n}
                className={`px-2 py-0.5 text-[11px] font-mono font-semibold transition-colors ${limit === n ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {n}
              </button>
            ))}
          </div>
          {data?.scan_day && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="stage2-leaderboard-scan-day">
              Scan {data.scan_day}
            </span>
          )}
        </div>
      </div>
      <p className="text-[13px] text-muted-foreground">
        Non-dividend stocks passing <AbbrTip term="SEPA">Minervini SEPA Lite</AbbrTip> — the longest-held{' '}
        <AbbrTip term="Trend Template">Stage 2</AbbrTip> uptrends and this week&apos;s fresh breakouts.
      </p>
      <p className="text-[12px] text-muted-foreground mb-3" data-testid="stage2-leaderboard-strip-legend">
        The strip under each name is its 90-day Trend Template pass/fail history (green = passed, bright green = passed with the EPS gate too, red = failed).
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Stage 2 leaders…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Column
              icon={<Award className="w-3.5 h-3.5 text-amber-500" />}
              title="Longest-running Stage 2"
              hint={`${data.veteran_min_days}+ scan days holding all 8 rules`}
              rows={veterans}
              empty={`No name has held Stage 2 for ${data.veteran_min_days}+ scan days yet — the nightly scan began ${data.scan_started || 'recently'}. Check back as the streaks build.`}
              testId="stage2-veterans"
              {...rowProps}
            />
            <Column
              icon={<Flame className="w-3.5 h-3.5 text-rose-500" />}
              title="Fresh breakouts"
              hint={`Entered Stage 2 within the last ${data.fresh_max_days} scan days`}
              rows={fresh}
              empty="No new Stage 2 entries this week."
              testId="stage2-fresh"
              {...rowProps}
            />
          </div>
          <p className="text-[12px] text-muted-foreground text-center mt-3" data-testid="stage2-leaderboard-footer">
            Recalculated nightly after the close.{' '}
            <Link to="/screener?filter=non_dividend&preset=minervini_sepa_lite" className="font-semibold text-sky-600 dark:text-sky-400 hover:underline" data-testid="stage2-leaderboard-open-screener">
              Full list →
            </Link>
          </p>
        </>
      )}
    </div>
  );
}

function HistoryStrip({ ticker, history }) {
  if (!Array.isArray(history) || history.length === 0) return null;
  const h = history.slice(-90);
  const passed = h.filter((x) => x.pass).length;
  return (
    <div className="flex items-center gap-1.5 mt-1" data-testid={`stage2-history-strip-${ticker}`}>
      <div
        className="flex gap-px h-1.5 flex-1 max-w-[180px] rounded-sm overflow-hidden bg-muted/40"
        role="img"
        aria-label={`${ticker} Trend Template pass/fail over the last ${h.length} scan days: ${passed} passed`}
        title={`Last ${h.length} scan day${h.length === 1 ? '' : 's'} · ${passed}/${h.length} passed the Trend Template`}
      >
        {Array.from({ length: 90 - h.length }).map((_, i) => <span key={`pad-${i}`} className="flex-1" />)}
        {h.map((x) => (
          <span
            key={x.d}
            title={`${x.d} · ${x.pass ? 'passed' : 'failed'}${x.rs != null ? ` · RS ${x.rs}` : ''}${x.sepa ? ' · SEPA' : ''}`}
            className={`flex-1 min-w-[2px] ${x.pass ? (x.sepa ? 'bg-emerald-500' : 'bg-emerald-400/70') : 'bg-rose-400/70'}`}
          />
        ))}
      </div>
      <span className="text-[9px] font-mono text-muted-foreground shrink-0">{passed}/{h.length}</span>
    </div>
  );
}

function Column({ icon, title, hint, rows, empty, testId, onStockClick, watchlist, onToggleWatchlist, livePrices }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5 border-b border-border/60 pb-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{icon}{title}</span>
        <span className="text-[10px] text-muted-foreground hidden sm:inline">{hint}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground py-3" data-testid={`${testId}-empty`}>{empty}</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {rows.map((row, idx) => {
            const m = row.minervini || {};
            const inWatchlist = watchlist.includes(row.ticker);
            const live = livePrices?.[row.ticker];
            const price = live?.price != null ? live.price : row.price;
            return (
              <li key={row.ticker} className="flex items-center gap-2 py-1.5 hover:bg-muted/40 transition-colors rounded-sm" data-testid={`${testId}-row-${row.ticker}`}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleWatchlist?.(row.ticker); }}
                  className="p-1 rounded hover:bg-muted/60 transition-colors shrink-0"
                  data-testid={`stage2-watchlist-star-${row.ticker}`}
                  aria-label={inWatchlist ? `Remove ${row.ticker} from watchlist` : `Add ${row.ticker} to watchlist`}
                  aria-pressed={inWatchlist}
                >
                  <Star className={`w-4 h-4 transition-colors ${inWatchlist ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} />
                </button>
                <span className="w-4 text-right font-mono tabular-nums text-muted-foreground text-[11px] shrink-0">{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => onStockClick?.(row.ticker)}
                      className="text-sm font-semibold hover:underline underline-offset-2"
                      data-testid={`stage2-leaderboard-row-${row.ticker}`}
                    >
                      {row.ticker}
                    </button>
                    <MinerviniBadge ticker={row.ticker} m={m} kind="rs" />
                    {m.tight_base && <MinerviniBadge ticker={row.ticker} m={m} kind="tight" />}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">{row.sector || row.name || '—'}</div>
                  <HistoryStrip ticker={row.ticker} history={m.history} />
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono tabular-nums text-[12px] font-semibold" data-testid={`stage2-days-${row.ticker}`}>
                    {m.stage2_days ?? 0}<span className="text-muted-foreground font-normal text-[10px]"> d</span>
                  </div>
                  <div className={`font-mono tabular-nums text-[11px] ${live?.price != null ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                    {price != null ? `$${Number(price).toFixed(2)}` : '—'}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
